import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import TipCard from "@/components/TipCard";
import { Button } from "@/components/ui/button";
import { Sparkles, Heart, Lock, ArrowRight, Loader2 } from "lucide-react";

export default function PublicLanding() {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tips/preview");
        setTips(data.tips);
      } catch {
        // no-op
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 glass-nav border-b">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-5 py-3">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-brand">
            <div className="w-10 h-10 rounded-2xl bg-[#E8621A] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="leading-tight">
              <p className="font-heading font-bold text-[#1B2A4A] text-base">Surviving Together</p>
              <p className="text-[11px] text-[#1B2A4A]/60">Thriving Together</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="public-nav-login" className="rounded-full text-[#1B2A4A] hover:bg-[#1B2A4A]/5 font-semibold">
                Sign in
              </Button>
            </Link>
            <Link to="/register">
              <Button data-testid="public-nav-register" className="rounded-full bg-[#E8621A] hover:bg-[#D05615] text-white font-semibold px-5">
                Join free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-10">
        {/* Hero */}
        <section>
          <p className="text-sm font-semibold text-[#2A9D8F] tracking-widest uppercase">Community Feed · Preview</p>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1B2A4A] leading-[1.05]">
            Real tips from <span className="text-[#E8621A]">real neighbors.</span>
          </h1>
          <p className="mt-4 text-[17px] text-[#1B2A4A]/75 max-w-xl leading-relaxed">
            Join a free community helping each other navigate the cost of everyday life — savings tips, a simple budget tracker, and a Relaxation Room to catch your breath.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/register">
              <Button data-testid="hero-cta-register" className="h-12 px-6 rounded-full bg-[#E8621A] hover:bg-[#D05615] text-white font-semibold text-base transition-colors duration-200">
                Join the community <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" data-testid="hero-cta-login" className="h-12 px-6 rounded-full text-[#1B2A4A] hover:bg-[#1B2A4A]/5 font-semibold text-base">
                I already have an account
              </Button>
            </Link>
          </div>
        </section>

        {/* Preview tips */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-4 h-4 text-[#E8621A]" />
            <p className="font-heading font-bold text-xl text-[#1B2A4A]">A peek at what's inside</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#E8621A]" /></div>
          ) : tips.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-3xl border border-dashed border-[#1B2A4A]/15">
              <p className="text-[#1B2A4A]/60">New tips coming in soon. Join to be first.</p>
            </div>
          ) : (
            <motion.div
              className="space-y-4"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.08 } }, hidden: {} }}
              data-testid="public-tips-preview"
            >
              {tips.map((t) => (
                <div key={t.id} className="pointer-events-none select-none">
                  <TipCard tip={t} />
                </div>
              ))}
            </motion.div>
          )}
        </section>

        {/* Gate prompt */}
        <section
          data-testid="register-gate"
          className="relative overflow-hidden rounded-3xl bg-[#1B2A4A] text-white p-8 sm:p-10"
        >
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#E8621A]/30 blur-3xl" />
          <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-[#2A9D8F]/25 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold tracking-widest uppercase">
              <Lock className="w-3 h-3" /> Members only
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold leading-tight">
              Join free to see every tip, post your own, and open the Toolbox.
            </h2>
            <p className="mt-3 text-white/80 max-w-xl leading-relaxed">
              It takes under a minute. No payment. Browse the full feed, use the budget tracker, and visit the Relaxation Room — all on the Free tier.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register">
                <Button data-testid="gate-cta-register" className="h-12 px-6 rounded-full bg-[#E8621A] hover:bg-[#D05615] text-white font-semibold text-base">
                  Create a free account <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" data-testid="gate-cta-login" className="h-12 px-6 rounded-full text-white hover:bg-white/10 font-semibold text-base">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <footer className="pt-4 text-center text-xs text-[#1B2A4A]/50 space-y-2">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/guidelines" data-testid="public-footer-guidelines" className="text-[#1B2A4A]/70 hover:text-[#E8621A] font-semibold transition-colors duration-200">
              Community Guidelines
            </Link>
            <span className="text-[#1B2A4A]/30">·</span>
            <Link to="/privacy" data-testid="public-footer-privacy" className="text-[#1B2A4A]/70 hover:text-[#E8621A] font-semibold transition-colors duration-200">
              Privacy Policy
            </Link>
            <span className="text-[#1B2A4A]/30">·</span>
            <Link to="/terms" data-testid="public-footer-terms" className="text-[#1B2A4A]/70 hover:text-[#E8621A] font-semibold transition-colors duration-200">
              Terms of Service
            </Link>
          </div>
          <p>Powered by SocialImpactCLE</p>
        </footer>
      </main>
    </div>
  );
}
